import React from 'react';
import { 
    Box, 
    CircularProgress, 
    Snackbar, 
    Alert, 
    Typography, 
    Paper,
    Divider,
    Chip
} from '@mui/material';
import { 
    CloudUpload as CloudUploadIcon,
    DateRange as DateRangeIcon,
    Assessment as AssessmentIcon
} from '@mui/icons-material';
import CityManagerDialog from '../CityManagerDialog';
import FileUploader from './FileUploader';
import DateSelector from './DateSelector';
import SummaryDisplay from './SummaryDisplay';
import ActionButtons from './ActionButtons';
import { useCompare } from './useCompare';

const Compare: React.FC = () => {
    const {
        salesReport,
        archiveOrders,
        isLoading,
        selectedDate,
        isProcessed,
        sums,
        citySums,
        snackbar,
        openCityManager,
        cityTerminals,
        setSalesReport,
        setArchiveOrders,
        setOpenCityManager,
        handleCloseSnackbar,
        handleFileChange,
        clearFile,
        handleStart,
        handleDownload,
        handleClearAll,
        handleDateChange
    } = useCompare();

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
            {/* Секция 1: Загрузка файлов */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CloudUploadIcon color="primary" />
                    <Typography variant="h6">
                        1. Загрузка файлов
                    </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 300 }}>
                        <FileUploader
                            label="Sales Report"
                            fileName={salesReport}
                            fileId="salesReport"
                            isLoading={isLoading}
                            onFileChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                handleFileChange(e, setSalesReport, 'salesReport')
                            }
                            onClear={handleClearAll}
                        />
                        {salesReport && (
                            <Chip 
                                label="✓ Загружен" 
                                color="success" 
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 300 }}>
                        <FileUploader
                            label="Archive Orders"
                            fileName={archiveOrders}
                            fileId="archiveOrders"
                            isLoading={isLoading}
                            disabled={!salesReport}
                            onFileChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                                handleFileChange(e, setArchiveOrders, 'archiveOrders')
                            }
                            onClear={() => clearFile('archiveOrders', setArchiveOrders, 'archiveOrders')}
                        />
                        {archiveOrders && (
                            <Chip 
                                label="✓ Загружен" 
                                color="success" 
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {!salesReport && (
                            <Chip 
                                label="Сначала загрузите Sales Report" 
                                color="default" 
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                </Box>
            </Paper>

            {/* Секция 2: Настройки и запуск */}
            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <DateRangeIcon color="primary" />
                    <Typography variant="h6">
                        2. Настройки и запуск
                    </Typography>
                </Box>

                <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2 
                }}>
                    <DateSelector
                        selectedDate={selectedDate}
                        onDateChange={handleDateChange}
                    />
                    
                    <ActionButtons
                        salesReport={salesReport}
                        archiveOrders={archiveOrders}
                        isLoading={isLoading}
                        isProcessed={isProcessed}
                        onStart={handleStart}
                        onDownload={handleDownload}
                        onOpenCityManager={() => setOpenCityManager(true)}
                    />
                </Box>
            </Paper>

            {/* Индикатор загрузки */}
            {isLoading && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    alignItems: 'center', 
                    py: 4 
                }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress size={40} sx={{ mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                            Обработка файлов...
                        </Typography>
                    </Box>
                </Box>
            )}

            {/* Секция 3: Результаты */}
            <Paper elevation={2} sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <AssessmentIcon color="primary" />
                    <Typography variant="h6">
                        3. Результаты сравнения
                    </Typography>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                <SummaryDisplay
                    sums={sums}
                    citySums={citySums}
                    cityTerminals={cityTerminals}
                />
            </Paper>

            {/* Уведомления */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {/* Диалог управления городами */}
            <CityManagerDialog 
                open={openCityManager}
                onClose={() => setOpenCityManager(false)}
            />
        </Box>
    );
};

export default Compare;